import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import { startOfMonth } from 'date-fns';
import Excursions from './Excursions';
import Edukacijos from './Edukacijos';
import ExhibitionCalendar from './ExhibitionCalendar';
import ExhibitionReports from './ExhibitionReports';
import ExhibitionActivityLog from './ExhibitionActivityLog';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_BASE_URL } from '../config/api';

interface ExhibitionPanel {
  id: string;
  name: string;
  description: string;
  content: string;
  order: number;
  isActive: boolean;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  updater?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface PanelFormData {
  name: string;
  description: string;
  content: string;
  order: number;
  isActive: boolean;
  image?: File;
}

const Exhibition: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [panels, setPanels] = useState<ExhibitionPanel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPanel, setEditingPanel] = useState<ExhibitionPanel | null>(null);
  const [formData, setFormData] = useState<PanelFormData>({
    name: '',
    description: '',
    content: '',
    order: 0,
    isActive: true
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullSizeImage, setFullSizeImage] = useState<{ url: string; alt: string } | null>(null);
  const [fullSizeImageLoading, setFullSizeImageLoading] = useState(false);

  const isAdmin = hasPermission('exhibition.edit') || hasPermission('exhibition.delete');
  const canSeeActivityLog = hasPermission('exhibition.logs');
  type ExhibitionSection = 'edukacijos' | 'excursions' | 'reports' | 'logs';
  const [activeSection, setActiveSection] = useState<ExhibitionSection>('edukacijos');
  const [filterMonth, setFilterMonth] = useState(() => startOfMonth(new Date()));

  useEffect(() => {
    if (activeSection === 'reports' && !isAdmin) {
      setActiveSection('edukacijos');
    }
    if (activeSection === 'logs' && !canSeeActivityLog) {
      setActiveSection('edukacijos');
    }
  }, [isAdmin, canSeeActivityLog, activeSection]);

  const fetchPanels = async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin ? '/exhibition/admin' : '/exhibition';
      const response = await fetch(getApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch panels');
      }

      const data = await response.json();
      setPanels(data.panels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch panels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPanels();
  }, [isAdmin]);

  const handlePanelChange = (panelId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panelId : false);
  };

  const handleOpenDialog = (panel?: ExhibitionPanel) => {
    if (panel) {
      setEditingPanel(panel);
      setFormData({
        name: panel.name,
        description: panel.description,
        content: panel.content,
        order: panel.order,
        isActive: panel.isActive
      });
      setImagePreview(panel.imageUrl ? `${API_BASE_URL}${panel.imageUrl}` : null);
    } else {
      setEditingPanel(null);
      setFormData({
        name: '',
        description: '',
        content: '',
        order: 0,
        isActive: true
      });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPanel(null);
    setFormData({
      name: '',
      description: '',
      content: '',
      order: 0,
      isActive: true
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleFormChange = (field: keyof PanelFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'order' ? parseInt(value as string) || 0 : value
    }));
  };

  // Paste handler for images: uploads to /api/exhibition/upload and inserts markdown image reference
  const handleContentPaste = useCallback(async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        try {
          const uploadForm = new FormData();
          uploadForm.append('image', file);
          const response = await fetch(getApiUrl('/exhibition/upload'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: uploadForm
          });
          if (!response.ok) {
            throw new Error('Upload failed');
          }
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            content: `${prev.content}${prev.content ? '\n' : ''}![](${data.imageUrl})\n`
          }));
        } catch (err) {
          setError('Failed to paste image');
        }
      }
    }
  }, [setFormData]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (imageUrl: string, altText: string) => {
    const fullUrl = `${API_BASE_URL}${imageUrl}`;
    setFullSizeImageLoading(true);
    setFullSizeImage({ url: fullUrl, alt: altText });
  };

  const renderContentWithImages = (content: string) => {
    const nodes: React.ReactNode[] = [];
    const imageRegex = /!\[(.*?)\]\(([^\s)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    const pushText = (text: string) => {
      if (!text) return;
      const parts = text.split('\n');
      parts.forEach((part, idx) => {
        if (part) nodes.push(<span key={`t-${keyIndex++}`}>{part}</span>);
        if (idx < parts.length - 1) nodes.push(<br key={`br-${keyIndex++}`} />);
      });
    };

    while ((match = imageRegex.exec(content)) !== null) {
      const [full, alt, url] = match;
      const start = match.index;
      const end = start + full.length;
      pushText(content.slice(lastIndex, start));
      const isAbsolute = /^https?:\/\//i.test(url);
      const src = isAbsolute ? url : `${API_BASE_URL}${url}`;
      const clickPath = isAbsolute ? url.replace(API_BASE_URL, '') : url;
      nodes.push(
        <img
          key={`img-${keyIndex++}`}
          src={src}
          alt={alt || 'Image'}
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            objectFit: 'contain',
            borderRadius: '8px',
            display: 'block',
            marginTop: '8px',
            marginBottom: '8px',
            cursor: 'pointer'
          }}
          onClick={() => handleImageClick(clickPath, alt || 'Image')}
        />
      );
      lastIndex = end;
    }
    pushText(content.slice(lastIndex));
    return nodes;
  };

  const handleCloseFullSizeImage = () => {
    setFullSizeImage(null);
  };

  // Handle keyboard events for full-size image
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && fullSizeImage) {
        handleCloseFullSizeImage();
      }
    };

    if (fullSizeImage) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullSizeImage]);

  const handleSubmit = async () => {
    try {
      const endpoint = editingPanel 
        ? `/exhibition/${editingPanel.id}`
        : '/exhibition';
      
      const method = editingPanel ? 'PUT' : 'POST';
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('order', formData.order.toString());
      formDataToSend.append('isActive', formData.isActive.toString());
      
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      const response = await fetch(getApiUrl(endpoint), {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save panel');
      }

      handleCloseDialog();
      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save panel');
    }
  };

  const handleDelete = async (panelId: string) => {
    if (!window.confirm('Are you sure you want to delete this panel?')) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/exhibition/${panelId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete panel');
      }

      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete panel');
    }
  };

  const handleToggleActive = async (panel: ExhibitionPanel) => {
    try {
      const response = await fetch(getApiUrl(`/exhibition/${panel.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...panel,
          isActive: !panel.isActive
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update panel');
      }

      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update panel');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ExhibitionCalendar currentMonth={filterMonth} onMonthChange={setFilterMonth} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeSection}
          onChange={(_, newValue: ExhibitionSection) => setActiveSection(newValue)}
        >
          <Tab value="edukacijos" label={t('exhibition.tabs.edukacijos', 'Edukacijos')} />
          <Tab value="excursions" label={t('exhibition.tabs.excursions', 'Ekskursijos')} />
          {isAdmin && (
            <Tab value="reports" label={t('exhibition.tabs.reports', 'Veiklos ataskaita')} />
          )}
          {canSeeActivityLog && (
            <Tab value="logs" label={t('exhibition.tabs.logs', 'Veiklos žurnalas')} />
          )}
        </Tabs>
      </Box>

      <Box sx={{ display: activeSection === 'edukacijos' ? 'block' : 'none' }}>
        <Edukacijos embedded filterMonth={filterMonth} />
      </Box>
      <Box sx={{ display: activeSection === 'excursions' ? 'block' : 'none' }}>
        <Excursions embedded />
      </Box>
      {activeSection === 'reports' && isAdmin && <ExhibitionReports embedded />}
      {activeSection === 'logs' && canSeeActivityLog && <ExhibitionActivityLog embedded />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {panels.length === 0 ? null : (
        <Box>
          {panels.map((panel) => (
            <Accordion
              key={panel.id}
              expanded={expandedPanel === panel.id}
              onChange={handlePanelChange(panel.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                      {panel.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {panel.description}
                    </Typography>
                  </Box>
                  
                  {isAdmin && (
                    <Chip
                      label={panel.isActive ? 'Active' : 'Inactive'}
                      color={panel.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                {/* Add admin actions at the top of AccordionDetails */}
                {isAdmin && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(panel)}
                      title={panel.isActive ? "Hide panel" : "Show panel"}
                    >
                      {panel.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(panel)}
                      title="Edit panel"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(panel.id)}
                      color="error"
                      title="Delete panel"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
                
                <Box sx={{ whiteSpace: 'pre-wrap' }}>
                  {renderContentWithImages(panel.content)}
                </Box>
                
                {panel.imageUrl && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Tooltip title="Click to view full size" placement="top">
                      <img
                        src={`${API_BASE_URL}${panel.imageUrl}`}
                        alt={panel.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onClick={() => handleImageClick(panel.imageUrl!, panel.name)}
                      />
                    </Tooltip>
                  </Box>
                )}
                
                {isAdmin && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created by: {panel.creator.firstName} {panel.creator.lastName} on{' '}
                      {new Date(panel.createdAt).toLocaleDateString()}
                    </Typography>
                    {panel.updater && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Updated by: {panel.updater.firstName} {panel.updater.lastName} on{' '}
                        {new Date(panel.updatedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPanel ? 'Edit Panel' : 'Add New Panel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={handleFormChange('name')}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleFormChange('description')}
              multiline
              rows={2}
              required
            />
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={handleFormChange('content')}
              onPaste={handleContentPaste}
              multiline
              rows={6}
              required
            />
            
            {/* Image Upload Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Panel Image (Optional)
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              {imagePreview && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Order"
                type="number"
                value={formData.order}
                onChange={handleFormChange('order')}
                inputProps={{ min: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleFormChange('isActive')}
                  />
                }
                label="Active"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPanel ? 'Update' : 'Create'}
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Full Size Image Dialog */}
       <Dialog
         open={!!fullSizeImage}
         onClose={handleCloseFullSizeImage}
         maxWidth={false}
         fullWidth
         PaperProps={{
           sx: {
             maxWidth: '90vw',
             maxHeight: '90vh',
             backgroundColor: 'transparent',
             boxShadow: 'none'
           }
         }}
       >
         <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: 'transparent' }}>
           {fullSizeImage && (
             <Box sx={{ position: 'relative' }}>
               {fullSizeImageLoading && (
                 <Box sx={{ 
                   display: 'flex', 
                   justifyContent: 'center', 
                   alignItems: 'center', 
                   height: '50vh' 
                 }}>
                   <CircularProgress />
                 </Box>
               )}
               <img
                 src={fullSizeImage.url}
                 alt={fullSizeImage.alt}
                 style={{
                   maxWidth: '100%',
                   maxHeight: '90vh',
                   objectFit: 'contain',
                   borderRadius: '8px',
                   display: fullSizeImageLoading ? 'none' : 'block'
                 }}
                 onLoad={() => setFullSizeImageLoading(false)}
                 onError={() => setFullSizeImageLoading(false)}
               />
               <IconButton
                 onClick={handleCloseFullSizeImage}
                 sx={{
                   position: 'absolute',
                   top: 8,
                   right: 8,
                   backgroundColor: 'rgba(0, 0, 0, 0.5)',
                   color: 'white',
                   '&:hover': {
                     backgroundColor: 'rgba(0, 0, 0, 0.7)'
                   }
                 }}
               >
                 <CloseIcon />
               </IconButton>
             </Box>
           )}
         </DialogContent>
       </Dialog>
     </Box>
   );
 };

export default Exhibition; 